import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  MapPin, Building2, Home as HomeIcon, Trees, CheckCircle2, ArrowRight, Phone, Mail,
  Award, Leaf, Wallet, Search, PenTool, FileText, HardHat, KeyRound
} from 'lucide-react';
import ScrollReveal from '../components/ScrollReveal';
import SEO from '../components/SEO';
import JsonLd from '../components/JsonLd';
import { projectsData } from '../projectsData';
import useScrollIlluminate from '../hooks/useScrollIlluminate';
import './CityPage.css';

// Grilles animées au scroll (référence stable, hors composant).
const ILLUMINATE_GROUPS = [
  ['.city-services-grid', '.city-service-card', null],
  ['.city-project-cards', '.city-project-card', null],
  ['.city-process-grid', '.city-process-step', '.city-process-line, .city-process-line-v'],
  ['.city-why-grid', '.city-why-card', null],
];

// `city` est fourni directement par la route (voir App.jsx) — plus aucun parsing d'URL.
export default function CityPage({ city, t, language }) {
  const tc = t?.cityPage || {};
  const isEN = language === 'en';
  const rootRef = useRef(null);

  // Illumination séquentielle des cards + tracé de la ligne, pilotés au scroll.
  useScrollIlluminate(rootRef, ILLUMINATE_GROUPS, [language, city]);

  if (!city) {
    return null;
  }

  const services = [
    {
      icon: <Building2 size={32} strokeWidth={1.5} />,
      title: tc.service1Title || 'Habitat Collectif',
      items: tc.service1Items || [
        'Logements collectifs et résidences',
        'Habitat intermédiaire et groupé',
        'Conformité RE2020 et BBC',
        'Intégration urbaine et paysagère'
      ]
    },
    {
      icon: <HomeIcon size={32} strokeWidth={1.5} />,
      title: tc.service2Title || 'Habitat Individuel',
      items: tc.service2Items || [
        'Villas contemporaines sur mesure',
        'Maisons passives et BBC',
        'Extensions et rénovations',
        'Architecture méditerranéenne'
      ]
    },
    {
      icon: <Trees size={32} strokeWidth={1.5} />,
      title: tc.service3Title || 'Tertiaire & Équipements',
      items: tc.service3Items || [
        'Études urbaines et ZAC',
        'Équipements publics (ERP)',
        'Bâtiments administratifs',
        'Équipements sportifs et culturels'
      ]
    }
  ];

  // Cartes « pourquoi » avec icônes (plus de trait rouge cheap)
  const whyCards = [
    {
      icon: <Award size={22} strokeWidth={1.7} />,
      title: "Architecte inscrite à l'Ordre",
      titleEN: 'Registered architect',
      text: "Daniela Parito Sorrenti, architecte diplômée de Rome, possède 30 ans d'expérience en conception architecturale complexe et gestion de projets en Occitanie.",
      textEN: 'Daniela Parito Sorrenti, an architect trained in Rome, has 30 years of experience in complex architectural design and project management across Occitanie.'
    },
    {
      icon: <Leaf size={22} strokeWidth={1.7} />,
      title: 'Expertise RE2020 & BBC',
      titleEN: 'RE2020 & BBC expertise',
      text: `Maîtrise complète des normes environnementales les plus exigeantes : RE2020, BBC, bâtiment passif et HQE. Vos projets à ${city.name} bénéficieront des meilleures performances énergétiques.`,
      textEN: `Full command of the most demanding environmental standards: RE2020, BBC, passive building and HQE. Your projects in ${city.name} will benefit from the best energy performance.`
    },
    {
      icon: <MapPin size={22} strokeWidth={1.7} />,
      title: 'Ancrage local Hérault',
      titleEN: 'Rooted in the Hérault',
      text: "Basé à Montpellier depuis 2014, le cabinet DP Architecture intervient dans tout le département de l'Hérault et connaît parfaitement les contraintes réglementaires et paysagères locales.",
      textEN: 'Based in Montpellier since 2014, DP Architecture works across the entire Hérault department and knows the local regulatory and landscape constraints inside out.'
    },
    {
      icon: <Wallet size={22} strokeWidth={1.7} />,
      title: 'Respect des budgets',
      titleEN: 'Budgets respected',
      text: "Engagement ferme sur le respect des enveloppes budgétaires. Chaque projet fait l'objet d'un suivi rigoureux des coûts, de la conception à la livraison.",
      textEN: 'A firm commitment to respecting budget envelopes. Every project is subject to rigorous cost control, from design to delivery.'
    }
  ];

  // Notre accompagnement — process réel (synthèse de la page Missions)
  const processSteps = isEN
    ? [
        { icon: <Search size={22} strokeWidth={1.6} />, title: 'Listening & feasibility', text: 'Analysis of your plot, the urban context and local regulations, then definition of the brief and the budget.' },
        { icon: <PenTool size={22} strokeWidth={1.6} />, title: 'Design', text: 'From sketch to preliminary and detailed design — a bespoke project optimised for RE2020 performance and comfort.' },
        { icon: <FileText size={22} strokeWidth={1.6} />, title: 'Building permit', text: 'Preparation and submission of the planning application, then consultation and selection of the contractors.' },
        { icon: <HardHat size={22} strokeWidth={1.6} />, title: 'Site supervision', text: 'Works management, control of costs and deadlines, quality monitoring and an environmentally clean site.' },
        { icon: <KeyRound size={22} strokeWidth={1.6} />, title: 'Handover', text: 'Checking the conformity of the works and supporting you through to the final handover.' }
      ]
    : [
        { icon: <Search size={22} strokeWidth={1.6} />, title: 'Écoute & faisabilité', text: "Analyse de votre terrain, du contexte urbain et de la réglementation locale, puis définition du programme et du budget." },
        { icon: <PenTool size={22} strokeWidth={1.6} />, title: 'Conception', text: "De l'esquisse à l'avant-projet : un projet sur mesure, optimisé pour la performance énergétique (RE2020) et le confort." },
        { icon: <FileText size={22} strokeWidth={1.6} />, title: 'Permis de construire', text: "Constitution et dépôt du dossier de permis, puis consultation et choix des entreprises." },
        { icon: <HardHat size={22} strokeWidth={1.6} />, title: 'Suivi de chantier', text: "Pilotage des travaux, maîtrise des coûts et des délais, contrôle de la qualité et chantier propre." },
        { icon: <KeyRound size={22} strokeWidth={1.6} />, title: 'Réception & livraison', text: "Vérification de la conformité des ouvrages et accompagnement jusqu'à la remise des clés." }
      ];

  const seoTitle = isEN
    ? `Architect in ${city.name} | Hérault (34) - DP Architecture`
    : `Architecte à ${city.name} | Hérault (34) - DP Architecture`;
  const seoDescription = city.description;
  const seoKeywords = city.keywords;

  const allProjects = [...city.localProjects, ...city.nearbyProjects];
  const baseUrl = 'https://dp-architecture.com';
  const pageUrl = `${baseUrl}/architecte-${city.slug}`;

  const projectById = (id) => projectsData.find(p => p.id === id);

  // JSON-LD : LocalBusiness
  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    '@id': pageUrl,
    name: `Daniela Parito Architecture – Architecte à ${city.name}`,
    description: city.description,
    url: pageUrl,
    telephone: '+33614902513',
    email: 'daniela.parito@gmail.com',
    image: `${baseUrl}/og-image.jpg`,
    logo: `${baseUrl}/images/logo/dplogo.webp`,
    priceRange: '€€',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '663 Rue du pré aux clercs',
      addressLocality: 'Montpellier',
      postalCode: '34000',
      addressRegion: 'Hérault',
      addressCountry: 'FR'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 43.6047,
      longitude: 3.8772
    },
    areaServed: [
      { '@type': 'City', name: city.name, containedInPlace: { '@type': 'AdministrativeArea', name: 'Hérault' } },
      { '@type': 'AdministrativeArea', name: 'Hérault' },
      { '@type': 'AdministrativeArea', name: 'Occitanie' }
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Services architecture',
      itemListElement: [
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Logements collectifs', description: 'Conception de résidences et logements collectifs RE2020 et BBC' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Maisons individuelles', description: 'Villas contemporaines, maisons passives et BBC' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Équipements publics', description: 'Équipements sportifs, culturels et administratifs' } }
      ]
    },
    founder: { '@type': 'Person', name: 'Daniela Parito Sorrenti', jobTitle: 'Architecte' },
    foundingDate: '2014',
    sameAs: [`${baseUrl}`]
  };

  // FAQ bilingue — sert à la fois au rendu visible (accordéon) et au JSON-LD FAQPage
  const ctx = isEN ? (city.contextEN || city.context) : city.context;
  const realisationsProches = allProjects.map(p => p.label).join(', ');
  const interventionAnswer = isEN
    ? (city.localProjects.length > 0
        ? `Yes. Daniela Parito Architecture has already worked in ${city.name} and delivered: ${city.localProjects.map(p => p.label).join(', ')}. The practice, based in Montpellier (663 Rue du pré aux clercs, 34000), covers the entire Hérault department.`
        : `Yes, Daniela Parito Architecture works in ${city.name} and throughout the Hérault department.${realisationsProches ? ` Our nearest projects: ${realisationsProches}.` : ''} The practice is based in Montpellier (663 Rue du pré aux clercs, 34000).`)
    : (city.localProjects.length > 0
        ? `Oui. Daniela Parito Architecture est déjà intervenue à ${city.name} et y a livré : ${city.localProjects.map(p => p.label).join(', ')}. Le cabinet, basé à Montpellier (663 Rue du pré aux clercs, 34000), couvre tout le département de l'Hérault.`
        : `Oui, Daniela Parito Architecture intervient à ${city.name} et dans tout le département de l'Hérault.${realisationsProches ? ` Nos réalisations les plus proches : ${realisationsProches}.` : ''} Le cabinet est basé à Montpellier (663 Rue du pré aux clercs, 34000).`);

  const faqItems = isEN
    ? [
        { q: `Why use an architect in ${city.name}?`, a: `${ctx} Daniela Parito Architecture brings 30 years of experience in collective housing, single-family homes and public facilities, from design to delivery.` },
        { q: `How much does an architect cost in ${city.name}?`, a: `An architect's fees in ${city.name} depend on the nature and scale of the project. DP Architecture offers a free initial consultation to assess your project and propose an assignment suited to your needs and budget.` },
        { q: `Does DP Architecture work in ${city.name}?`, a: interventionAnswer },
        { q: `What is the RE2020 standard for a project in ${city.name}?`, a: `RE2020 is the environmental regulation applicable since 2022 to new buildings in France, including in ${city.name}. It imposes stronger energy performance and carbon requirements. DP Architecture masters this regulation and integrates it from the design stage.` }
      ]
    : [
        { q: `Pourquoi faire appel à un architecte à ${city.name} ?`, a: `${ctx} Daniela Parito Architecture apporte 30 ans d'expérience en logements collectifs, maisons individuelles et équipements publics, de la conception à la livraison.` },
        { q: `Quel est le coût d'un architecte à ${city.name} ?`, a: `Les honoraires d'un architecte à ${city.name} dépendent de la nature et de l'ampleur du projet. DP Architecture propose une première consultation gratuite pour évaluer votre projet et vous proposer une mission adaptée à vos besoins et à votre budget.` },
        { q: `DP Architecture intervient-il à ${city.name} ?`, a: interventionAnswer },
        { q: `Qu'est-ce que la norme RE2020 pour un projet à ${city.name} ?`, a: `La RE2020 est la réglementation environnementale applicable depuis 2022 aux constructions neuves en France, y compris à ${city.name}. Elle impose des performances énergétiques et un bilan carbone renforcés. DP Architecture maîtrise cette réglementation et l'intègre dès la conception de votre projet.` }
      ];

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map(it => ({
      '@type': 'Question',
      name: it.q,
      acceptedAnswer: { '@type': 'Answer', text: it.a }
    }))
  };

  // JSON-LD : BreadcrumbList
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Accueil', item: baseUrl },
      { '@type': 'ListItem', position: 2, name: `Architecte à ${city.name}`, item: pageUrl }
    ]
  };

  const intro = isEN ? (city.introEN || city.intro) : city.intro;
  const context = isEN ? (city.contextEN || city.context) : city.context;

  return (
    <div className="city-page" ref={rootRef}>
      <JsonLd data={localBusinessSchema} />
      <JsonLd data={faqSchema} />
      <JsonLd data={breadcrumbSchema} />
      <SEO
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        image={city.image ? `${baseUrl}${city.image}` : undefined}
      />

      {/* ===== HERO ===== */}
      <section
        className="city-hero"
        style={city.image ? { backgroundImage: `url(${city.image})` } : {}}
      >
        <div className="city-hero-overlay" />
        <div className="city-hero-content">
          <ScrollReveal>
            <p className="city-hero-region">
              <MapPin size={16} />
              {tc.region || 'Hérault (34) — Occitanie'}
            </p>
            <h1 className="city-hero-title">
              {tc.heroTitle || 'Architecte à'} {city.name}
            </h1>
            <p className="city-hero-subtitle">
              {tc.heroSubtitle || "Cabinet Daniela Parito Architecture — 30 ans d'expérience dans l'Hérault"}
            </p>
            <div className="city-hero-cta">
              <Link to="/contact" className="city-btn-primary">
                {tc.ctaQuote || 'Demander un devis gratuit'}
                <ArrowRight size={18} />
              </Link>
              <Link to="/projects" className="city-btn-secondary">
                {tc.ctaProjects || 'Voir nos réalisations'}
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <div className="page-container">

        {/* ===== INTRO ===== */}
        <ScrollReveal>
          <section className="city-intro-section">
            <div className="city-intro-text">
              <h2 className="city-section-title">
                {tc.introTitle || 'DP Architecture à'} {city.name}
              </h2>
              <p className="city-intro-body">{intro}</p>
              <p className="city-intro-body city-intro-context">{context}</p>
            </div>
            <div className="city-intro-stats">
              <div className="city-stat">
                <span className="city-stat-number">30+</span>
                <span className="city-stat-label">{tc.statExp || "ans d'expérience"}</span>
              </div>
              <div className="city-stat">
                <span className="city-stat-number">150+</span>
                <span className="city-stat-label">{tc.statProjects || 'projets livrés'}</span>
              </div>
              <div className="city-stat">
                <span className="city-stat-number">RE2020</span>
                <span className="city-stat-label">{tc.statCert || 'expertise certifiée'}</span>
              </div>
            </div>
          </section>
        </ScrollReveal>

        {/* ===== SERVICES ===== */}
        <section className="city-services-section">
          <ScrollReveal>
            <h2 className="city-section-title city-section-title--centered">
              {tc.servicesTitle || 'Nos services à'} {city.name} {tc.servicesAnd || "et dans l'Hérault"}
            </h2>
            <p className="city-section-intro">
              {tc.servicesIntro || "Daniela Parito Architecture intervient sur l'ensemble des typologies architecturales dans le département de l'Hérault."}
            </p>
          </ScrollReveal>

          <div className="city-services-grid">
            {services.map((service, idx) => (
              <div className="city-service-card" key={idx}>
                <div className="city-service-icon">{service.icon}</div>
                <h3 className="city-service-title">{service.title}</h3>
                <ul className="city-service-list">
                  {service.items.map((item, iIdx) => (
                    <li key={iIdx}>
                      <CheckCircle2 size={15} strokeWidth={2} />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* ===== PROJETS LIÉS (cartes visuelles) ===== */}
        {allProjects.length > 0 && (
          <section className="city-projects-section">
            <ScrollReveal>
              <h2 className="city-section-title">
                {city.localProjects.length > 0
                  ? `${tc.projectsLocalTitle || 'Nos réalisations à'} ${city.name} ${tc.projectsLocalAnd || 'et à proximité'}`
                  : `${tc.projectsNearbyTitle || 'Nos réalisations à proximité de'} ${city.name}`}
              </h2>
              <p className="city-section-intro">
                {tc.projectsIntro || 'Découvrez nos projets de référence dans votre secteur.'}
              </p>
            </ScrollReveal>
            <div className="city-project-cards">
              {allProjects.map((project, idx) => {
                const data = projectById(project.id);
                const img = data?.image || city.image;
                const isLocal = idx < city.localProjects.length;
                return (
                  <Link to={`/project/${project.id}`} key={`${project.id}-${idx}`} className="city-project-card">
                    <div className="city-project-card-media">
                      {img && <img src={img} alt={project.label} loading="lazy" />}
                      {isLocal && (
                        <span className="city-project-badge">
                          {isEN ? `In ${city.name}` : `À ${city.name}`}
                        </span>
                      )}
                    </div>
                    <div className="city-project-card-body">
                      {data?.location && (
                        <span className="city-project-card-loc">
                          <MapPin size={13} /> {data.location}
                        </span>
                      )}
                      <h3 className="city-project-card-title">{data?.title || project.label}</h3>
                      <span className="city-project-card-cta">
                        {isEN ? 'View project' : 'Voir le projet'}
                        <ArrowRight size={14} />
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
            <ScrollReveal>
              <div className="city-all-projects-cta">
                <Link to="/projects" className="city-btn-outline">
                  {tc.projectsCta || 'Voir tous nos projets'}
                  <ArrowRight size={18} />
                </Link>
              </div>
            </ScrollReveal>
          </section>
        )}

        {/* ===== NOTRE ACCOMPAGNEMENT (process) ===== */}
        <section className="city-process-section">
          <ScrollReveal>
            <h2 className="city-section-title city-section-title--centered">
              {isEN ? 'Our support, step by step' : 'Notre accompagnement, étape par étape'}
            </h2>
            <p className="city-section-intro">
              {isEN
                ? 'From the first sketch to handover, we guide you through every stage of your project.'
                : "De la première esquisse à la remise des clés, nous vous accompagnons à chaque étape de votre projet."}
            </p>
          </ScrollReveal>
          <div className="city-process-track">
            <svg
              className="city-process-line"
              viewBox="0 0 1200 110"
              preserveAspectRatio="none"
              aria-hidden="true"
              focusable="false"
            >
              <path
                className="city-process-line-base"
                d="M0,46 C120,30 200,30 320,46 C440,62 520,62 640,46 C760,30 840,30 960,46 C1020,58 1090,46 1150,46 L1183,46"
              />
              {/* flèche au repos : grise, comme le rail */}
              <path className="city-process-line-arrowhead-base" d="M1183,38 L1203,46 L1183,54 Z" />
              <path
                className="city-process-line-glow"
                d="M0,46 C120,30 200,30 320,46 C440,62 520,62 640,46 C760,30 840,30 960,46 C1020,58 1090,46 1150,46 L1183,46"
              />
              <path
                className="city-process-line-arrowhead"
                d="M1183,38 L1203,46 L1183,54 Z"
              />
            </svg>

            {/* Petit écran : les cartes s'empilent, le fil passe à la verticale */}
            <svg
              className="city-process-line-v"
              viewBox="0 0 56 1000"
              preserveAspectRatio="none"
              aria-hidden="true"
              focusable="false"
            >
              <path
                className="city-process-line-base"
                d="M28,0 C16,100 40,180 28,290 C16,400 40,480 28,580 C16,680 40,760 28,860 C20,920 30,940 28,960 L28,975"
              />
              <path className="city-process-line-arrowhead-base" d="M20,975 L28,995 L36,975 Z" />
              <path
                className="city-process-line-glow"
                d="M28,0 C16,100 40,180 28,290 C16,400 40,480 28,580 C16,680 40,760 28,860 C20,920 30,940 28,960 L28,975"
              />
              <path className="city-process-line-arrowhead city-process-line-arrowhead--v" d="M20,975 L28,995 L36,975 Z" />
            </svg>
            <div className="city-process-grid">
              {processSteps.map((step, idx) => (
                <div className="city-process-step" key={idx}>
                  <div className="city-process-icon">{step.icon}</div>
                  <span className="city-process-num">{String(idx + 1).padStart(2, '0')}</span>
                  <h3 className="city-process-title">{step.title}</h3>
                  <p className="city-process-text">{step.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== POURQUOI DP ARCHITECTURE ===== */}
        <section className="city-why-section">
          <ScrollReveal>
            <h2 className="city-section-title city-section-title--centered">
              {tc.whyTitle || 'Pourquoi choisir DP Architecture à'} {city.name} ?
            </h2>
          </ScrollReveal>
          <div className="city-why-grid">
            {whyCards.map((item, idx) => (
              <div className="city-why-card" key={idx}>
                <div className="city-why-icon">{item.icon}</div>
                <div>
                  <h3 className="city-why-title">{isEN ? item.titleEN : item.title}</h3>
                  <p className="city-why-text">{isEN ? item.textEN : item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ===== FAQ VISIBLE (alignée au schéma) ===== */}
        <section className="city-faq-section">
          <ScrollReveal>
            <h2 className="city-section-title city-section-title--centered">
              {isEN ? `Frequently asked questions — architect in ${city.name}` : `Questions fréquentes — architecte à ${city.name}`}
            </h2>
          </ScrollReveal>
          <div className="city-faq-list">
            {faqItems.map((it, idx) => (
              <ScrollReveal key={idx} delay={idx * 0.05}>
                <details className="city-faq-item" name="cityfaq">
                  <summary className="city-faq-question">{it.q}</summary>
                  <div className="city-faq-answer"><p>{it.a}</p></div>
                </details>
              </ScrollReveal>
            ))}
          </div>
        </section>

        {/* ===== CTA CONTACT ===== */}
        <ScrollReveal>
          <section className="city-cta-section">
            <div className="city-cta-content">
              <h2 className="city-cta-title">
                {tc.ctaTitle || 'Votre projet à'} {city.name}{tc.ctaTitleEnd || ' ? Parlons-en !'}
              </h2>
              <p className="city-cta-text">
                {tc.ctaText1 || "Contactez Daniela Parito Architecture pour une première consultation gratuite. Nous intervenons à"} {city.name} {tc.ctaText2 || "et dans tout le département de l'Hérault (34)."}
              </p>
              <div className="city-cta-contacts">
                <a href="tel:+33614902513" className="city-contact-item">
                  <Phone size={20} />
                  06 14 90 25 13
                </a>
                <a href="mailto:daniela.parito@gmail.com" className="city-contact-item">
                  <Mail size={20} />
                  daniela.parito@gmail.com
                </a>
              </div>
              <Link to="/contact" className="city-btn-primary city-btn-primary--large">
                {tc.ctaSend || 'Envoyer un message'}
                <ArrowRight size={20} />
              </Link>
            </div>
          </section>
        </ScrollReveal>

      </div>
    </div>
  );
}
